import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { modifier } from 'ember-modifier';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import mapClickOverlayConditional from '../modifiers/map-click-overlay-conditional';

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface PlaceMarker {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  emoji?: string;
}

interface Signature {
  Args: {
    markers: PlaceMarker[];
    fullscreen?: boolean;
    /** Optional user location from parent - if provided, shows user marker */
    userLocation?: Coordinates | null;
    /** When true, map is always interactive (no "click to interact" overlay) */
    alwaysInteractive?: boolean;
  };
  Element: HTMLDivElement;
}

const markerIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl:
    'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function createUserLocationIcon(): L.DivIcon {
  return L.divIcon({
    html: `<div style="width:18px;height:18px;background:#2563eb;border:3px solid white;border-radius:50%;box-shadow:0 0 0 3px rgba(37,99,235,0.3)"></div>`,
    className: '',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -12],
  });
}

export default class PlaceMap extends Component<Signature> {
  @tracked isLocating = false;

  private map: L.Map | null = null;
  private userMarker: L.Marker | null = null;

  /**
   * Show the user marker if location is provided
   */
  @action
  showUserLocation(location: Coordinates) {
    if (!this.map) return;

    const userLatLng: L.LatLngExpression = [
      location.latitude,
      location.longitude,
    ];

    // Update or create user marker
    if (this.userMarker) {
      this.userMarker.setLatLng(userLatLng);
    } else {
      this.userMarker = L.marker(userLatLng, {
        icon: createUserLocationIcon(),
      })
        .bindPopup('Your location')
        .addTo(this.map);
    }

    // Zoom to user location
    this.map.setView(userLatLng, 14);
  }

  mountMap = modifier((element: HTMLDivElement, [markers]: [PlaceMarker[]]) => {
    const map = L.map(element);
    this.map = map;
    map.scrollWheelZoom.disable();

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    const bounds: L.LatLngExpression[] = [];
    const clusters = L.markerClusterGroup();

    for (const marker of markers) {
      const latLng: L.LatLngExpression = [marker.latitude, marker.longitude];
      const icon = marker.emoji ? createEmojiIcon(marker.emoji) : markerIcon;
      const popup = `<a href="/items/${encodeURIComponent(marker.id)}">${escapeHtml(marker.name)}</a>`;
      clusters.addLayer(L.marker(latLng, { icon }).bindPopup(popup));
      bounds.push(latLng);
    }

    map.addLayer(clusters);

    if (bounds.length === 0) {
      map.setView([20, 0], 2);
    } else if (bounds.length === 1) {
      map.setView(bounds[0] as L.LatLngExpression, 14);
    } else {
      map.fitBounds(bounds as L.LatLngBoundsExpression, { padding: [24, 24] });
    }

    requestAnimationFrame(() => map.invalidateSize());

    // Show user location marker if provided by parent
    if (this.args.userLocation) {
      this.showUserLocation(this.args.userLocation);
    }

    // Re-enable scroll wheel zoom when the overlay is clicked
    const onActivate = () => map.scrollWheelZoom.enable();
    const onDeactivate = () => map.scrollWheelZoom.disable();
    element.addEventListener('click', onActivate);
    element.addEventListener('mouseleave', onDeactivate);

    return () => {
      this.map = null;
      this.userMarker = null;
      element.removeEventListener('click', onActivate);
      element.removeEventListener('mouseleave', onDeactivate);
      map.remove();
    };
  });

  <template>
    <div class="relative">
      <div class="absolute top-4 right-4 z-[1000]">
        <button
          type="button"
          class="btn btn-circle btn-neutral shadow-lg"
          disabled={{this.isLocating}}
        >
          {{#if this.isLocating}}
            <span class="loading loading-spinner loading-sm"></span>
          {{else}}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          {{/if}}
        </button>
      </div>
      <div
        class={{if @fullscreen "w-full" "h-120 w-full rounded-lg"}}
        style={{if
          @fullscreen
          "height: 100vh; min-height: 100vh; width: 100%;"
        }}
        {{this.mountMap @markers}}
        {{mapClickOverlayConditional @alwaysInteractive}}
        ...attributes
      ></div>
    </div>
  </template>
}

function createEmojiIcon(emoji: string): L.DivIcon {
  return L.divIcon({
    html: `<div class="place-emoji-marker"><span aria-hidden="true">${emoji}</span></div>`,
    className: '',
    iconSize: [38, 38],
    iconAnchor: [19, 19],
    popupAnchor: [0, -22],
  });
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
